import { useEffect } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { useQueryClient } from 'react-query'
import { getUniqueIdentifier } from 'utils/sessionStorage'

const NOTIFICATIONS_CHANNEL_NAME = 'NotificationsChannel'

interface Payload {
  operation?: 'auto_mark_as_read' | 'keep_in_sync'
  session_uuid: string | undefined | null
}

export const useNotificationsChannel = () => {
  const websocketConnection = useWebsocketConnection()
  const { notifications } = useRootStore()
  const queryClient = useQueryClient()
  const auth = useAuth()
  const currentWorkspace = useCurrentWorkspace()
  const currentSessionUuid = getUniqueIdentifier()

  useEffect(() => {
    if (!websocketConnection || !auth || !currentWorkspace) return

    const subscriptionParams = {
      channel: NOTIFICATIONS_CHANNEL_NAME,
      user_id: auth.id,
      workspace_id: currentWorkspace.id,
    }
    const subscription = websocketConnection.subscriptions.create(subscriptionParams, {
      received: ({ operation, session_uuid }: Payload) => {
        const isSameSession = session_uuid && session_uuid === currentSessionUuid
        const keepInSync = operation === 'keep_in_sync'
        const autoMarkAsRead = operation === 'auto_mark_as_read'

        if (keepInSync && isSameSession) return null

        if (keepInSync || autoMarkAsRead) {
          notifications.clear()

          // refetch read notifications (to display on the 'read' list)
          queryClient.invalidateQueries(['useNotifications', true, undefined, currentWorkspace.key])
        }

        // refetch unread and unseen notifications (to display count on top of bell)
        queryClient.invalidateQueries(['useNotifications', false, false, currentWorkspace.key])

        // refetch unread notifications (to display on the 'unread' list)
        queryClient.invalidateQueries(['useNotifications', false, undefined, currentWorkspace.key])
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [websocketConnection, notifications, auth, currentWorkspace, queryClient, currentSessionUuid])
}
